// Links, addresses and phone numbers in text written by people or models: which ones are ours.
// Shared by the FAQ rules (faq.ts), the checks on what reaches a model or a user (apps/api
// lib/prompt-data.ts) and campaign texts (apps/api lib/campaigns.ts, and the admin's composer).
// "Ours" is decided on the parsed host, never on how the text ends, so
// https://evil.example/@gaintplay.com or https://gaintplay.com@evil.example is foreign.

/** Links, bare domains (common endings) and email addresses. */
export const LINKS =
  /\b(?:https?:\/\/|www\.)[^\s<>"]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\b(?:[a-z0-9-]+\.)+(?:com|net|org|io|co|me|app|xyz|info|biz|gg|ly|to|in|np|us|uk|ru|cn|top|site|online|link|click|live|shop|store|cc|tk|ml|ga|cf|gq|ai|dev|page|pro|vip|win|bet|money|cash)\b(?:\/[^\s<>"]*)?/gi;

const EMAIL = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/;
const OUR_EMAIL = /^[\w.+-]+@(?:[\w-]+\.)*gaintplay\.com$/i;

/** Whether one LINKS match points at gaintplay.com (or a subdomain), with no user name in it. */
export function isOurAddress(found: string): boolean {
  // Sentence punctuation that LINKS keeps on a link ("…/terms." or "(…/faq)").
  const s = found.replace(/[.,;:!?)\]}'"’]+$/, '');
  if (EMAIL.test(s)) return OUR_EMAIL.test(s);
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
  } catch {
    return false;
  }
  const host = url.hostname.toLowerCase();
  return (
    !url.username && !url.password && (host === 'gaintplay.com' || host.endsWith('.gaintplay.com'))
  );
}

/** The first link, domain or email address in `text` that isn't ours, or null. */
export function foreignAddress(text: string): string | null {
  for (const m of text.matchAll(LINKS)) if (!isOurAddress(m[0])) return m[0];
  return null;
}

/** The first phone number in `text` (9+ digits; dates like 2026-10-24 aren't), or null. */
export function phoneNumber(text: string): string | null {
  for (const m of text.matchAll(/\+?\d[\d\s().-]{7,}\d/g)) {
    const digits = m[0].replace(/\D/g, '');
    if (digits.length >= 9 && !/^\d{4}-\d{2}-\d{2}/.test(m[0].trim())) return m[0].trim();
  }
  return null;
}
